
import React, { useState, useEffect } from 'react';
import { WeatherCondition } from '@/types/weather';

interface WeatherBackgroundProps {
  location: string;
  condition: WeatherCondition;
}

export const WeatherBackground: React.FC<WeatherBackgroundProps> = ({ location, condition }) => {
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getWeatherLabel = () => {
    switch (condition) {
      case 'clear': return 'sunny clear sky';
      case 'partlyCloudy': return 'partly cloudy sky';
      case 'cloudy': return 'cloudy sky';
      case 'rain': return 'rainy weather';
      case 'heavyRain': return 'heavy rain storm';
      case 'thunderstorm': return 'thunderstorm lightning';
      case 'snow': return 'snow winter landscape';
      case 'fog': return 'foggy misty landscape';
      case 'wind': return 'windy landscape';
      case 'alert': return 'weather warning storm';
      default: return 'cloudy sky';
    }
  };

  const getBackgroundColorOverlay = () => {
    switch (condition) {
      case 'clear': return 'bg-gradient-to-b from-blue-400/60 to-blue-300/60';
      case 'partlyCloudy': return 'bg-gradient-to-b from-blue-400/60 to-blue-200/60';
      case 'cloudy': return 'bg-gradient-to-b from-blue-300/60 to-gray-200/60';
      case 'rain': return 'bg-gradient-to-b from-blue-600/60 to-blue-400/60';
      case 'heavyRain': return 'bg-gradient-to-b from-blue-800/60 to-blue-600/60';
      case 'thunderstorm': return 'bg-gradient-to-b from-gray-800/60 to-blue-800/60';
      case 'snow': return 'bg-gradient-to-b from-gray-700/60 to-blue-500/60';
      case 'fog': return 'bg-gradient-to-b from-gray-600/70 to-gray-400/70';
      case 'wind': return 'bg-gradient-to-b from-blue-300/60 to-gray-200/60';
      case 'alert': return 'bg-gradient-to-b from-red-500/60 to-red-400/60';
      default: return 'bg-gradient-to-b from-blue-300/60 to-gray-200/60';
    }
  };

  const getFallbackImage = () => {
    // Versioni ad alta risoluzione e in formato verticale (1080x1920) per garantire piena copertura
    const fallbackImages: Record<WeatherCondition, string> = {
      clear: 'https://images.unsplash.com/photo-1619566085055-8da7e9f7a9fe?fit=crop&w=1080&h=1920&q=80',
      partlyCloudy: 'https://images.unsplash.com/photo-1595228140633-375fd1157875?fit=crop&w=1080&h=1920&q=80',
      cloudy: 'https://images.unsplash.com/photo-1531147646552-1eec36255e03?fit=crop&w=1080&h=1920&q=80',
      rain: 'https://images.unsplash.com/photo-1534274988757-c5560e0347c7?fit=crop&w=1080&h=1920&q=80',
      heavyRain: 'https://images.unsplash.com/photo-1530577197743-7adf14294584?fit=crop&w=1080&h=1920&q=80',
      thunderstorm: 'https://images.unsplash.com/photo-1429152937469-4f7309decda5?fit=crop&w=1080&h=1920&q=80',
      snow: 'https://images.unsplash.com/photo-1546514355-cd84298f330f?fit=crop&w=1080&h=1920&q=80',
      fog: 'https://images.unsplash.com/photo-1517966388562-19a1b220de5c?fit=crop&w=1080&h=1920&q=80',
      wind: 'https://images.unsplash.com/photo-1514163161169-8ec4815c8c0a?fit=crop&w=1080&h=1920&q=80',
      alert: 'https://images.unsplash.com/photo-1524591026454-c6890ec2307c?fit=crop&w=1080&h=1920&q=80',
    };
    
    return fallbackImages[condition];
  };

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      setIsLoading(true);
      try {
        // Specifichiamo esplicitamente "portrait vertical" nella query per ottenere immagini con orientamento verticale
        const query = `${location} ${getWeatherLabel()} landscape portrait vertical`;
        const encodedQuery = encodeURIComponent(query);
        const timestamp = new Date().getTime();
        // Richiediamo specificamente immagini di dimensione 1080x1920 (formato verticale per mobile)
        const imageUrl = `https://source.unsplash.com/featured/1080x1920/?${encodedQuery}&t=${timestamp}`;
        
        // Precarica l'immagine per verificare che si carichi correttamente
        const img = new Image();
        img.onload = () => {
          setBackgroundImage(img.src);
          setIsLoading(false);
        };
        img.onerror = () => {
          console.warn('Failed to load image from Unsplash, using fallback');
          setBackgroundImage(getFallbackImage());
          setIsLoading(false);
        };
        img.src = imageUrl;
        
        // Timeout di sicurezza per evitare attese infinite
        setTimeout(() => {
          if (isLoading) {
            console.warn('Image loading timeout, using fallback');
            setBackgroundImage(getFallbackImage());
            setIsLoading(false);
          }
        }, 8000);
      } catch (error) {
        console.error('Error loading background image:', error);
        setBackgroundImage(getFallbackImage());
        setIsLoading(false);
      }
    };

    fetchBackgroundImage();
  }, [condition, location]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {isLoading ? (
        <div className="w-full h-full min-h-[1500px] flex items-center justify-center bg-gray-100">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : backgroundImage && (
        <>
          <img 
            src={backgroundImage} 
            alt={`${location} ${getWeatherLabel()}`} 
            className="w-full h-full min-h-[1500px] object-cover"
          />
          <div className={`absolute inset-0 ${getBackgroundColorOverlay()}`}></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </>
      )}
    </div>
  );
};

export default WeatherBackground;
